<template>
  <div class="container">
    <div id="autocompleteSearch" />
  </div>
</template>

<script lang="jsx" setup>
import {
  h, Fragment, render, onMounted, onUnmounted, watch,
} from 'vue';
import { autocomplete } from '@algolia/autocomplete-js';
import EventBus from '@/event-bus';
import '@algolia/autocomplete-theme-classic';
import _ from '@/lodash';
import axios from 'axios';
import { useStore } from 'vuex';

const store = useStore();
let searchAutoComplete = {};

// Add a watcher to trigger updates when the search term is read from the browser url (e.g. when the user clicks the back button)
watch(
  () => store.state.search.query.s,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (newValue === '') {
        searchAutoComplete.setQuery(null);
        return;
      }
      searchAutoComplete.setQuery(newValue);
    }
  },
  { deep: true },
);

function debouncePromise(fn, time) {
  let timerId;

  return function debounced(...args) {
    if (timerId) {
      clearTimeout(timerId);
    }

    return new Promise((resolve) => {
      timerId = setTimeout(() => resolve(fn(...args)), time);
    });
  };
}

const placeholderText = (queryType) => {
  if (queryType === 'events') {
    return 'Start your event search...';
  }
  if (queryType === 'reviews') {
    return 'Start your review search...';
  }
  if (queryType === 'companies') {
    return 'Start your company search...';
  }
  return 'Start your apprenticeship search...';
};

const getResultsPromise = (query) => {
  if (query === '' || query.length < 2) return [];
  const cancellationTokens = [];
  const config = {
    suggestionsUrl: '/suggestions.php',
  };
  const { CancelToken } = axios;

  const source = CancelToken.source();

  cancellationTokens.push(source.cancel);

  const params = {
    params: { s: query, target: store.state.search.query.show },
    cancelToken: source.token,
  };

  return axios.get(config.suggestionsUrl, params);
};

const debounced = debouncePromise((items) => getResultsPromise(items), 300);

const submitSearch = (searchValue) => {
  // Related to new search url format
  const parsedUrl = new URL(window.location.href);
  parsedUrl.searchParams.set('s', searchValue);
  store.commit('search/updateSearchUrl', parsedUrl.pathname + parsedUrl.search);

  searchAutoComplete.setIsOpen(false);
  store.commit('search/updateQueryFilters', {
    filterValues: searchValue,
    filterName: 's',
  });
};

const renderSuggestion = (suggestion, searchValue) => {
  const name = _.cloneDeep(suggestion);
  const search = searchValue.replace(/[^\w\s]/gi, '');
  const regExp = new RegExp(search, 'ig');
  const matched = name.match(regExp);

  if (matched !== null) {
    return name.replace(regExp, `<strong>${matched[0]}</strong>`);
  }

  // We need to return the unformatted string if there aren't matches as
  // ES matches without punctuation and alternate name but this autocomplete doesn't
  // e.g. JP > J.P Morgan
  // e.g. Pricewaterhouse > PwC
  return name;
};

const ListItemHtml = (item, query) => {
  return (
      <div
          key={item.id}
          onClick={(event) => {
            event.stopPropagation();
            submitSearch(item.name, event);
          }}
      >
        <div className="aa-Item-meta">
          <div className="aa-Item-meta-title">
            <a
                v-html={renderSuggestion(item.name, query)}
            />
          </div>
        </div>
      </div>
  );
};

onMounted(() => {
  const { s, show } = store.state.search.query;

  EventBus.$on('bus:mobile-header:search', submitSearch);

  searchAutoComplete = autocomplete({
    container: '#autocompleteSearch',
    openOnFocus: false,
    initialState: {
      query: s,
    },
    placeholder: placeholderText(show),
    onSubmit(suggestion) {
      submitSearch(suggestion.state.query);
    },

    onReset() {
      const parsedUrl = new URL(window.location.href);
      parsedUrl.searchParams.delete('s');
      store.commit('search/updateSearchUrl', parsedUrl.pathname + parsedUrl.search);
      store.commit('search/updateQueryFilters', {
        filterValues: '',
        filterName: 's',
      });
    },
    getSources({ query }) {
      return debounced(
        query,
      ).then(
        ({ data }) => {
          return [
            {
              sourceId: 'roles',
              getItems() {
                if (data) {
                  const { jobRoles } = data.data;

                  if (jobRoles.length === 0) {
                    return [];
                  }

                  return [...jobRoles];
                }
                return [];
              },
              // Needed for keyboard navigation returns item to navigator option below
              getItemUrl({ item }) {
                return item;
              },
              templates: {
                header() {
                  return (
                      <div className="autosuggest__results_title autosuggest__results_title_roles">Roles</div>
                  );
                },
                item({ item }) {
                  return ListItemHtml(item, query);
                },
              },
            },
            // new
            {
              sourceId: 'companies',
              getItems() {
                if (data) {
                  const { companies } = data.data;

                  if (companies.length === 0) {
                    return [];
                  }

                  return [...companies];
                }
                return [];
              },
              // Needed for keyboard navigation returns item to navigator option below
              getItemUrl({ item }) {
                return item;
              },
              templates: {
                header() {
                  return (
                      <div className="autosuggest__results_title autosuggest__results_title_companies">Companies</div>
                  );
                },
                item({ item }) {
                  return ListItemHtml(item, query);
                },
              },
            },
          ];
        },
      ).catch((error) => {
        console.log(error);
      });
    },
    // Enables keyboard navigation and selection. getItemUrl passes the location slug this function
    navigator: {
      navigate({ item }) {
        console.log('Navigator', item);
        submitSearch(item.name);
      },
    },
    renderer: { createElement: h, Fragment, render },
  });
});

onUnmounted(() => {
  const domElement = document.querySelector('.aa-Autocomplete');
  if (domElement) {
    // in the production bundle the dom node is not removed automatically like it is in the dev bundle
    // must remove it manually
    domElement.remove();
  }
  EventBus.$off('bus:mobile-header:search', submitSearch);
  searchAutoComplete.destroy();
});
</script>

<style lang="scss" scoped>

:deep(.aa-Autocomplete) {

  .aa-InputWrapperPrefix {
    .aa-Label svg {
      fill: black;
    }

    .aa-ClearButton {
      display: none !important;
    }

    &:hover {
      background: #32b056;

      .aa-Label svg {
        fill: #fff !important;
      }
    }
  }
  .container .Button {
    height: auto;
  }

  .aa-InputWrapperPrefix {
    order: 5;
  }

  .aa-Form {
    border-radius: 6px;
    border: 1px solid #848281;;

    &:focus-within {
      outline: 2px solid #ea5b1b !important;
      border: 1px solid #ea5b1b !important;
      // border: none !important;
      box-shadow: none !important;
    }
  }

  .autosuggest__results_title {
    color: #848281;
    font-size: 13px;
    margin-left: 0;
    padding: 15px 13px 5px;
    border-top: 1px solid #e7e7e7;
    text-align: left;
    list-style: none;
  }

  .aa-InputWrapper {
    order: 1;
    position: relative;
    width: 100%;
    padding-left: 15px;

    .aa-Input {
      padding: 0 !important;
    }
  }
}

</style>
